import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, auth as authLib } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarCheck2, GraduationCap, User2, Award, BookOpenCheck, Printer, TrendingUp, MapPin, Loader2 } from "lucide-react";

/** Full lifetime training history for one employee — quizzes + event attendance + certificates.
 * Route: /training-history/:id (admin) or /my/history (employee self)
 */
export default function TrainingHistory({ self = false }) {
  const { id: paramId } = useParams();
  const { branding, user } = useBranding();
  const brand = branding?.theme_color || "#0EA5E9";
  const empDbId = self ? user?.emp_db_id : paramId;

  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Reset previous error/data whenever the target id changes (fixes race with async user load in `self` mode).
    setErr("");
    setData(null);
    // Only surface "No employee selected" when we are NOT self OR the branding user has finished loading.
    if (!empDbId) {
      if (!self || user !== null) setErr("No employee selected");
      return;
    }
    api.get(`/employees/${empDbId}/training-history`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Couldn't load history"));
  }, [empDbId, self, user]);

  if (err) return <div className="p-8 text-center text-slate-500">{err}</div>;
  if (!data) return <div className="p-16 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-sky-500"/></div>;

  const emp = data.employee;

  return (
    <div className="p-6 lg:p-10 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Training Attendance Card</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1" data-testid="history-emp-name">{emp.name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
            <span className="font-mono">{emp.emp_id}</span>
            {emp.designation && <span>·  {emp.designation}</span>}
            {emp.email && <span>·  {emp.email}</span>}
          </div>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="history-print-btn"><Printer className="h-4 w-4 mr-1"/>Print</Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="history-summary">
        <SummaryTile icon={GraduationCap} label="Quizzes Completed" value={data.summary.total_quizzes_completed} color="sky"/>
        <SummaryTile icon={CalendarCheck2} label="Events Attended" value={data.summary.total_events_attended} color="emerald"/>
        <SummaryTile icon={TrendingUp} label="Avg Quiz Score" value={`${data.summary.avg_quiz_score_pct}%`} color="amber"/>
      </div>

      {/* Quiz history */}
      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <BookOpenCheck className="h-4 w-4" style={{ color: brand }}/>
            <span className="font-display font-bold text-lg text-slate-900">Quiz History</span>
            <Badge variant="secondary" className="bg-slate-100 text-slate-700 ml-auto">{data.quiz_history.length}</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="history-quizzes">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="py-2 px-4">Quiz</th>
                  <th className="py-2 px-4">Phase</th>
                  <th className="py-2 px-4">Score</th>
                  <th className="py-2 px-4">Completed</th>
                  <th className="py-2 px-4">Certificate</th>
                </tr>
              </thead>
              <tbody>
                {data.quiz_history.length === 0 && (
                  <tr><td colSpan="5" className="py-8 text-center text-slate-400">No completed quizzes.</td></tr>
                )}
                {data.quiz_history.map(q => (
                  <tr key={q.id} className="border-b border-slate-100">
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{q.quiz_title}</div>
                      {q.topic && <div className="text-xs text-slate-500 mt-0.5">{q.topic}</div>}
                    </td>
                    <td className="py-3 px-4">
                      {q.phase ? <Badge variant="secondary" className={q.phase === "post" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}>{q.phase.toUpperCase()}</Badge> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-bold text-slate-900">{q.percentage}%</div>
                      <div className="text-xs text-slate-500">{q.score}/{q.max_score}</div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-slate-600">
                      {new Date(q.completed_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      {q.percentage >= 50 ? (
                        <a href={`/api/certificate/${q.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-600 hover:underline" data-testid={`cert-link-${q.id}`}>
                          <Award className="h-3.5 w-3.5"/>Download
                        </a>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Event attendance history */}
      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <CalendarCheck2 className="h-4 w-4 text-emerald-500"/>
            <span className="font-display font-bold text-lg text-slate-900">Event Attendance</span>
            <Badge variant="secondary" className="bg-slate-100 text-slate-700 ml-auto">{data.event_history.length}</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="history-events">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="py-2 px-4">Event</th>
                  <th className="py-2 px-4">Venue</th>
                  <th className="py-2 px-4">Trainer</th>
                  <th className="py-2 px-4">Attended At</th>
                </tr>
              </thead>
              <tbody>
                {data.event_history.length === 0 && (
                  <tr><td colSpan="4" className="py-8 text-center text-slate-400">No event attendance yet.</td></tr>
                )}
                {data.event_history.map(a => (
                  <tr key={a.id} className="border-b border-slate-100">
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{a.event.title}</div>
                      {a.event.start_datetime && <div className="text-xs text-slate-500 mt-0.5">{new Date(a.event.start_datetime).toLocaleDateString()}</div>}
                    </td>
                    <td className="py-3 px-4">
                      {a.event.venue ? <span className="inline-flex items-center gap-1 text-slate-700"><MapPin className="h-3 w-3 text-slate-400"/>{a.event.venue}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4">
                      {a.event.trainer_name ? <span className="inline-flex items-center gap-1 text-slate-700"><User2 className="h-3 w-3 text-slate-400"/>{a.event.trainer_name}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-slate-600">
                      {new Date(a.attended_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, color }) {
  const bg = {
    sky: "bg-sky-50 text-sky-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
  }[color];
  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-5">
        <div className={`h-10 w-10 rounded-xl grid place-items-center ${bg}`}><Icon className="h-5 w-5"/></div>
        <div className="mt-3 font-display font-black text-3xl text-slate-900">{value}</div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}
